import React from 'react';

/**
 * Building4ViewElevation - Shows Front, Back, Left, Right architectural views
 */
const Building4ViewElevation = ({ config, className = '' }) => {
  const {
    style = 'carport',
    width = 20,
    length = 21,
    legHeight = 8,
    roofStyle = 'pointed',
    roofPitch = '4/12',
    roofColor = '#C41E3A',
    trimColor = '#FFFFFF',
    sideColor = '#CCCCCC',
    garageDoors = [],
    windows = [],
    walkDoors = [],
    leanTos = []
  } = config || {};

  // Scale and sizing
  const scale = 8;
  const viewPadding = 20;
  const viewWidth = 200;
  const viewHeight = 150;
  
  // Calculate roof height based on pitch
  const pitchMap = { '2/12': 0.17, '3/12': 0.25, '4/12': 0.33, '5/12': 0.42, '6/12': 0.5 };
  const pitchRatio = pitchMap[roofPitch] || 0.33;
  const roofHeight = (width * scale / 2) * pitchRatio;

  // Get items on specific wall
  const getItemsOnWall = (items, wall) => {
    return items.filter(item => (item.wall || 'front') === wall);
  };

  // Render a single elevation view
  const renderElevation = (viewName, wallWidth, wallHeight, items) => {
    const scaledWidth = wallWidth * scale;
    const scaledHeight = wallHeight * scale;
    const startX = (viewWidth - scaledWidth) / 2;
    const startY = viewHeight - scaledHeight - 20;
    
    // Get doors and windows for this wall
    const wallGarageDoors = getItemsOnWall(garageDoors, viewName);
    const wallWindows = getItemsOnWall(windows, viewName);
    const wallWalkDoors = getItemsOnWall(walkDoors, viewName);
    
    // Render roof based on style and view
    const renderRoof = () => {
      const roofStartY = startY;
      
      if (roofStyle === 'pointed' || roofStyle === 'gable') {
        if (viewName === 'front' || viewName === 'back') {
          // Gable end view - shows the triangle
          return (
            <polygon
              points={`
                ${startX},${roofStartY}
                ${startX + scaledWidth / 2},${roofStartY - roofHeight}
                ${startX + scaledWidth},${roofStartY}
              `}
              fill={roofColor}
              stroke={trimColor}
              strokeWidth="2"
            />
          );
        } else {
          // Side view - shows the slope
          return (
            <polygon
              points={`
                ${startX},${roofStartY - roofHeight / 2}
                ${startX + scaledWidth},${roofStartY - roofHeight / 2}
                ${startX + scaledWidth},${roofStartY}
                ${startX},${roofStartY}
              `}
              fill={roofColor}
              stroke={trimColor}
              strokeWidth="2"
            />
          );
        }
      } else if (roofStyle === 'flat' || roofStyle === 'regular') {
        // Flat roof
        return (
          <rect
            x={startX - 5}
            y={roofStartY - 8}
            width={scaledWidth + 10}
            height={8}
            fill={roofColor}
            stroke={trimColor}
            strokeWidth="2"
          />
        );
      } else if (roofStyle === 'boxed_eave' || roofStyle === 'boxed-eave') {
        // Boxed eave - slight overhang
        return (
          <g>
            <rect
              x={startX - 8}
              y={roofStartY - 12}
              width={scaledWidth + 16}
              height={12}
              fill={roofColor}
              stroke={trimColor}
              strokeWidth="2"
            />
            <rect
              x={startX - 8}
              y={roofStartY - 16}
              width={scaledWidth + 16}
              height={4}
              fill={trimColor}
              stroke="#333"
              strokeWidth="1"
            />
          </g>
        );
      } else if (roofStyle === 'a_frame' || roofStyle === 'a-frame') {
        // A-frame - steep pitch
        const steepRoof = scaledHeight * 0.6;
        if (viewName === 'front' || viewName === 'back') {
          return (
            <polygon
              points={`
                ${startX - 10},${roofStartY}
                ${startX + scaledWidth / 2},${roofStartY - steepRoof}
                ${startX + scaledWidth + 10},${roofStartY}
              `}
              fill={roofColor}
              stroke={trimColor}
              strokeWidth="2"
            />
          );
        }
      }
      
      // Default - simple roof line
      return (
        <rect
          x={startX - 5}
          y={roofStartY - 6}
          width={scaledWidth + 10}
          height={6}
          fill={roofColor}
          stroke={trimColor}
          strokeWidth="2"
        />
      );
    };

    // Render garage doors
    const renderGarageDoors = () => {
      return wallGarageDoors.map((door, idx) => {
        const doorWidth = (door.width || 10) * scale * 0.4;
        const doorHeight = scaledHeight * 0.7;
        const position = door.position || 50;
        const doorX = startX + (scaledWidth * position / 100) - doorWidth / 2;
        const doorY = startY + scaledHeight - doorHeight;
        
        return (
          <g key={`garage-${idx}`}>
            <rect
              x={doorX}
              y={doorY}
              width={doorWidth}
              height={doorHeight}
              fill="#F5F5F5"
              stroke="#333"
              strokeWidth="2"
            />
            {/* Garage door panels */}
            {[1, 2, 3, 4].map(i => (
              <line
                key={i}
                x1={doorX}
                y1={doorY + (doorHeight * i / 5)}
                x2={doorX + doorWidth}
                y2={doorY + (doorHeight * i / 5)}
                stroke="#CCC"
                strokeWidth="1"
              />
            ))}
            {/* Window strip at top */}
            <rect
              x={doorX + 4}
              y={doorY + 4}
              width={doorWidth - 8}
              height={doorHeight * 0.15}
              fill="#87CEEB"
              stroke="#333"
              strokeWidth="1"
            />
          </g>
        );
      });
    };

    // Render windows
    const renderWindows = () => {
      return wallWindows.map((window, idx) => {
        const winWidth = (window.width || 3) * scale * 0.5;
        const winHeight = winWidth * 0.8;
        const position = window.position || (30 + idx * 25);
        const winX = startX + (scaledWidth * position / 100) - winWidth / 2;
        const winY = startY + scaledHeight * 0.3;
        
        return (
          <g key={`window-${idx}`}>
            <rect
              x={winX}
              y={winY}
              width={winWidth}
              height={winHeight}
              fill="#87CEEB"
              stroke={trimColor}
              strokeWidth="2"
            />
            {/* Window cross */}
            <line x1={winX + winWidth/2} y1={winY} x2={winX + winWidth/2} y2={winY + winHeight} stroke={trimColor} strokeWidth="2" />
            <line x1={winX} y1={winY + winHeight/2} x2={winX + winWidth} y2={winY + winHeight/2} stroke={trimColor} strokeWidth="2" />
          </g>
        );
      });
    };

    // Render walk doors
    const renderWalkDoors = () => {
      return wallWalkDoors.map((door, idx) => {
        const doorWidth = 3 * scale * 0.4;
        const doorHeight = scaledHeight * 0.55;
        const position = door.position || 80;
        const doorX = startX + (scaledWidth * position / 100) - doorWidth / 2;
        const doorY = startY + scaledHeight - doorHeight;
        
        return (
          <g key={`walk-${idx}`}>
            <rect
              x={doorX}
              y={doorY}
              width={doorWidth}
              height={doorHeight}
              fill="#8B4513"
              stroke="#333"
              strokeWidth="2"
            />
            {/* Door handle */}
            <circle
              cx={doorX + doorWidth * 0.8}
              cy={doorY + doorHeight * 0.5}
              r="2"
              fill="#FFD700"
            />
            {/* Small window in door */}
            <rect
              x={doorX + doorWidth * 0.2}
              y={doorY + doorHeight * 0.1}
              width={doorWidth * 0.6}
              height={doorHeight * 0.2}
              fill="#87CEEB"
              stroke="#333"
              strokeWidth="1"
            />
          </g>
        );
      });
    };

    // Render structural posts/columns
    const renderPosts = () => {
      const postWidth = 4;
      const posts = [];
      
      // Corner posts
      posts.push({ x: startX, y: startY });
      posts.push({ x: startX + scaledWidth - postWidth, y: startY });
      
      // Add middle posts for wider buildings
      if (wallWidth > 24) {
        const numMiddle = Math.floor(wallWidth / 12) - 1;
        for (let i = 1; i <= numMiddle; i++) {
          posts.push({ 
            x: startX + (scaledWidth * i / (numMiddle + 1)) - postWidth / 2, 
            y: startY 
          });
        }
      }
      
      return posts.map((post, idx) => (
        <rect
          key={`post-${idx}`}
          x={post.x}
          y={post.y}
          width={postWidth}
          height={scaledHeight}
          fill={trimColor}
          stroke="#333"
          strokeWidth="1"
        />
      ));
    };

    return (
      <svg viewBox={`0 0 ${viewWidth} ${viewHeight}`} className="w-full h-full">
        {/* Sky gradient background */}
        <defs>
          <linearGradient id={`sky-${viewName}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#87CEEB" />
            <stop offset="100%" stopColor="#E0F4FF" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill={`url(#sky-${viewName})`} />
        
        {/* Ground */}
        <rect
          x="0"
          y={viewHeight - 15}
          width={viewWidth}
          height="15"
          fill="#4A7C23"
        />
        <rect
          x="0"
          y={viewHeight - 18}
          width={viewWidth}
          height="3"
          fill="#5D9930"
        />
        
        {/* Main wall */}
        <rect
          x={startX}
          y={startY}
          width={scaledWidth}
          height={scaledHeight}
          fill={sideColor}
          stroke={trimColor}
          strokeWidth="2"
        />
        
        {/* Structural posts */}
        {renderPosts()}
        
        {/* Roof */}
        {renderRoof()}
        
        {/* Garage doors */}
        {renderGarageDoors()}
        
        {/* Windows */}
        {renderWindows()}
        
        {/* Walk doors */}
        {renderWalkDoors()}
        
        {/* Trim lines at base */}
        <line
          x1={startX}
          y1={startY + scaledHeight}
          x2={startX + scaledWidth}
          y2={startY + scaledHeight}
          stroke={trimColor}
          strokeWidth="3"
        />
        
        {/* View label */}
        <text
          x={viewWidth / 2}
          y={12}
          textAnchor="middle"
          fontSize="11"
          fontWeight="600"
          fill="#374151"
        >
          {viewName.charAt(0).toUpperCase() + viewName.slice(1)} View
        </text>
        
        {/* Dimension label */}
        <text
          x={viewWidth / 2}
          y={viewHeight - 4}
          textAnchor="middle"
          fontSize="9"
          fill="#374151"
        >
          {wallWidth}' wide × {wallHeight}' tall
        </text>
      </svg>
    );
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Building Elevations</h3>
        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
          {width}' × {length}' × {legHeight}' • {style}
        </span>
      </div>
      
      {/* 4-View Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Front View */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          {renderElevation('front', width, legHeight)}
        </div>
        
        {/* Back View */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          {renderElevation('back', width, legHeight)}
        </div>
        
        {/* Left View */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          {renderElevation('left', length, legHeight)}
        </div>
        
        {/* Right View */}
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          {renderElevation('right', length, legHeight)}
        </div>
      </div>
      
      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400 pt-3 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-1">
          <div className="w-4 h-3 rounded" style={{ backgroundColor: roofColor }}></div>
          <span>Roof</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-3 rounded" style={{ backgroundColor: sideColor }}></div>
          <span>Walls</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-3 rounded" style={{ backgroundColor: trimColor, border: '1px solid #999' }}></div>
          <span>Trim</span>
        </div>
        {garageDoors.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 bg-gray-100 border border-gray-400 rounded"></div>
            <span>Garage ({garageDoors.length})</span>
          </div>
        )}
        {windows.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-4 h-3 bg-sky-300 border border-gray-400 rounded"></div>
            <span>Windows ({windows.length})</span>
          </div>
        )}
        {walkDoors.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-3 h-4 bg-amber-700 border border-gray-400 rounded"></div>
            <span>Doors ({walkDoors.length})</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Building4ViewElevation;
